import React from 'react';
import * as stylex from '@stylexjs/stylex';
import '../theme/Colors.stylex';
import { ColorDanger, ColorNeutral } from '../theme/Colors.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight } from '../theme/Fonts.stylex';
import '../theme/Spaces.stylex';
import { Gap, Padding } from '../theme/Spaces.stylex';
import { TooltipIcon } from '../overlays/TooltipIcon';
import { TooltipSpan } from '../overlays/Tooltip';

// MARK: exported types
// ====================
export type Size = 'small' | 'medium' | 'large' | 'xlarge';
export type Tone = 'neutral' | 'neutral-ghost';
export type ValueTone = 'neutral' | 'danger';

// MARK: root
// ==========
type Props = {
  size?: Size;
  tone?: Tone;
  shrink?: boolean;
  labelBold?: boolean;
  label?: string;
  value?: string;
  valueTone?: ValueTone;
  valueTooltipText?: string;
};
function StatsCountComponent({
  size = 'large',
  tone = 'neutral-ghost',
  shrink = false,
  labelBold = false,
  label,
  value,
  valueTone = 'neutral',
  valueTooltipText
}: Props) {
  const valueRef = React.useRef(null);
  return <div {...stylex.props(styles.root, sizeStyles[size], toneStyles[tone], shrink && styles.shrink)}>
      <span {...stylex.props(styles.textCell, styles.labelCell, paddingBySizeStyles[size], shrink && styles.shrink, labelBold && styles.labelCell_bold, tone === 'neutral-ghost' && styles.labelCell_neutralGhost)}>
        {label}
      </span>
      <span {...stylex.props(styles.textCell, styles.valueCell, paddingBySizeStyles[size], shrink && styles.shrink, tone === 'neutral-ghost' && styles.valueCell_neutralGhost, valueTone === 'danger' && styles.valueCell_danger)} ref={valueRef}>
        {value}
        {valueTooltipText !== undefined && <TooltipIcon variation="quiet_info" iconSize={17} arrowed altTriggerRef={valueRef}>
            <TooltipSpan text={valueTooltipText} />
          </TooltipIcon>}
      </span>
    </div>;
}
const MemoizedStatsCountComponent = React.memo(StatsCountComponent);
export { MemoizedStatsCountComponent as StatsCount };
export type { Props as StatsCountProps };

// MARK: styles
// ============
const styles = {
  root: {
    "display-k1xSpc": "display-x78zum5",
    "flexGrow-kzQI83": "flexGrow-x1iyjqo2",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    "borderWidth-kMzoRj": "borderWidth-xvndefy",
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:97"
  },
  textCell: {
    "display-k1xSpc": "display-x78zum5",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "justifyContent-kjj79g": "justifyContent-xl56j7k",
    "flexGrow-kzQI83": "flexGrow-x1iyjqo2",
    "height-kZKoxP": "height-x5yr21d",
    "whiteSpace-khDVqt": "whiteSpace-xuxw1ft",
    "fontFamily-kMv6JI": "fontFamily-x111x8ze",
    "fontWeight-k63SB2": "fontWeight-x1fkahbb",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:105"
  },
  labelCell: {
    "display-k1xSpc": "display-x1lliihq",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "flex-kUk6DE": "flex-x1mbw22u",
    "alignContent-kfiyM8": "alignContent-xc26acl",
    "textAlign-k9WMMc": "textAlign-x2b8uid",
    "textOverflow-kg5iWk": "textOverflow-xlyipyv",
    "color-kMwMTN": "color-x1awj2ng",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:115"
  },
  labelCell_neutralGhost: {
    "color-kMwMTN": "color-x1w1nkkj",
    "borderRightStyle-kSWEuD": "borderRightStyle-x32b0ac",
    "borderRightWidth-kWqL5O": "borderRightWidth-xs1s249",
    "borderRightColor-k26BEO": "borderRightColor-x132ee8w",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:124"
  },
  labelCell_bold: {
    "fontWeight-k63SB2": "fontWeight-x5x31i3",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:130"
  },
  valueCell: {
    "flex-kUk6DE": "flex-x13y811e",
    "backgroundColor-kWkggS": "backgroundColor-x12peec7",
    "fontWeight-k63SB2": "fontWeight-x5x31i3",
    "gap-kOIVth": "gap-xw718zh",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:133"
  },
  valueCell_neutralGhost: {
    "borderLeftStyle-k4WBpm": "borderLeftStyle-x19ypqd9",
    "borderLeftWidth-kZ1KPB": "borderLeftWidth-xe0pwq",
    "borderLeftColor-kaZRDh": "borderLeftColor-x1xm9nig",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:139"
  },
  valueCell_danger: {
    "color-kMwMTN": "color-x75p7gn",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:144"
  },
  shrink: {
    "flexShrink-kmuXW": "flexShrink-xs83m0k",
    "flexGrow-kzQI83": "flexGrow-x1c4vz4f",
    "width-kzqmXN": "width-xeq5yr9",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:147"
  }
};
const toneStyles = {
  neutral: {
    "backgroundColor-kWkggS": "backgroundColor-xug7tn3",
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:155"
  },
  "neutral-ghost": {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:160"
  }
};
const sizeStyles = {
  small: {
    "height-kZKoxP": "height-x10w6t97",
    "fontSize-kGuDYH": "fontSize-x106xqen",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:168"
  },
  medium: {
    "height-kZKoxP": "height-x1vqgdyp",
    "fontSize-kGuDYH": "fontSize-x1egw0ox",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:172"
  },
  large: {
    "height-kZKoxP": "height-xsdox4t",
    "fontSize-kGuDYH": "fontSize-x1vcisby",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:176"
  },
  xlarge: {
    "height-kZKoxP": "height-xng8ra",
    "fontSize-kGuDYH": "fontSize-xabvga2",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:180"
  }
};
const paddingBySizeStyles = {
  small: {
    "paddingInline-kg3NbH": "paddingInline-x1tefuxw",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:187"
  },
  medium: {
    "paddingInline-kg3NbH": "paddingInline-xq8m5sq",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:190"
  },
  large: {
    "paddingInline-kg3NbH": "paddingInline-xjoyhec",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:193"
  },
  xlarge: {
    "paddingInline-kg3NbH": "paddingInline-x1yzrijw",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryStatsCount.tsx:196"
  }
};